import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../hooks";
import proofkitFilemakerService from "../../../services/proofkitFilemakerService.js";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui";
import { ZContacts } from "../../../schema/Contacts";

const CONTACTS_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_CONTACTS || "contacts";
const TEMPLATES_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_TEMPLATES || "templates";

export default function SingleMailForm() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);

  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    email: "",
    template_id: "",
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const templatesRes = await proofkitFilemakerService.getAllRecords(TEMPLATES_LAYOUT);
        setTemplates(Array.isArray(templatesRes?.data) ? templatesRes.data : []);
      } catch (err) {
        toast({
          title: "Error",
          description: "No se pudieron cargar plantillas desde FileMaker",
          variant: "destructive",
        });
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [toast]);

  const templatesOptions = useMemo(() => {
    return (Array.isArray(templates) ? templates : [])
      .map((r) => {
        const fieldData = r?.fieldData || {};
        const rawId = fieldData?.ClavePrincipal ?? r?.recordId ?? "";
        const id = String(rawId ?? "").trim();
        const rawNombre = fieldData?.nombre ?? id;
        const nombre = String(rawNombre ?? "").trim();
        return { id, nombre: nombre || id };
      })
      .filter((t) => !!t?.id);
  }, [templates]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const findContactRecordIdByEmail = async (email) => {
    try {
      const res1 = await proofkitFilemakerService.findRecords(CONTACTS_LAYOUT, {
        query: [{ email: `==${email}` }],
        limit: 1,
        offset: 1,
      });
      const r1 = Array.isArray(res1?.data) ? res1.data[0] : null;
      if (r1?.recordId) return String(r1.recordId);
    } catch (_) {}

    try {
      const res2 = await proofkitFilemakerService.findRecords(CONTACTS_LAYOUT, {
        query: [{ email }],
        limit: 1,
        offset: 1,
      });
      const r2 = Array.isArray(res2?.data) ? res2.data[0] : null;
      if (r2?.recordId) return String(r2.recordId);
    } catch (_) {}

    try {
      const res3 = await proofkitFilemakerService.getAllRecords(CONTACTS_LAYOUT);
      const rows = Array.isArray(res3?.data) ? res3.data : [];
      const normalized = String(email || "").trim().toLowerCase();
      const match = rows.find((r) => String(r?.fieldData?.email || "").trim().toLowerCase() === normalized);
      if (match?.recordId) return String(match.recordId);
    } catch (_) {}

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const nombre = String(formData?.nombre || "").trim();
    const apellido = String(formData?.apellido || "").trim();
    const email = String(formData?.email || "").trim();
    const templateId = String(formData?.template_id || "").trim();

    if (!nombre) {
      return toast({ title: "Falta nombre", description: "Debes indicar el nombre del contacto", variant: "destructive" });
    }

    if (!email) {
      return toast({ title: "Falta email", description: "Debes indicar el email del contacto", variant: "destructive" });
    }

    if (!validateEmail(email)) {
      return toast({ title: "Email inválido", description: "El formato del email no es válido", variant: "destructive" });
    }

    if (!templateId) {
      return toast({ title: "Falta plantilla", description: "Debes seleccionar una plantilla", variant: "destructive" });
    }

    try {
      setSaving(true);

      const contactFieldData = ZContacts
        .pick({ nombre: true, apellido: true, email: true })
        .partial()
        .parse({ nombre, apellido, email });

      let recordIdToUse = "";

      recordIdToUse = await findContactRecordIdByEmail(email);

      if (!recordIdToUse) {
        try {
          const created = await proofkitFilemakerService.createRecord(CONTACTS_LAYOUT, contactFieldData);
          if (created?.recordId) recordIdToUse = String(created.recordId);
        } catch (createErr) {
          const msg = String(createErr?.message || "");
          const msgLower = msg.toLowerCase();
          const isDuplicate =
            msg.includes("(504") ||
            msg.includes(" 504 ") ||
            msg.includes('"code":"504"') ||
            (msgLower.includes("504") && (msgLower.includes("not unique") || msgLower.includes("value in field is not unique")));
          if (isDuplicate) {
            recordIdToUse = await findContactRecordIdByEmail(email);
          } else {
            throw createErr;
          }
        }
      }

      if (!recordIdToUse) {
        throw new Error("No se pudo crear el contacto en FileMaker");
      }

      const scriptRes = await proofkitFilemakerService.runScript(CONTACTS_LAYOUT, recordIdToUse, "Enviarsingle", templateId);

      const scriptError = scriptRes?.scriptError;
      if (scriptError === undefined || scriptError === null || String(scriptError) !== "0") {
        throw new Error(`Script Enviarsingle retornó error ${scriptError ?? "sin respuesta"}`);
      }

      toast({ title: "Email enviado", description: "Se creó el contacto y se ejecutó el envío individual" });
      navigate("/campanas");
    } catch (err) {
      console.error("Error sending single mail:", err);
      toast({
        title: "Error",
        description: err?.message ? `No se pudo enviar el email: ${err.message}` : "No se pudo enviar el email",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Email individual</h1>
        <Button variant="outline" onClick={() => navigate("/campanas")} disabled={saving}>
          Volver
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Enviar email</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando plantillas...</div>
          ) : (
            <form className="space-y-6" onSubmit={onSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input id="nombre" name="nombre" value={formData.nombre} onChange={handleInputChange} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="apellido">Apellido</Label>
                  <Input id="apellido" name="apellido" value={formData.apellido} onChange={handleInputChange} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
                </div>

                <div className="space-y-2">
                  <Label>Plantilla *</Label>
                  <Select value={formData.template_id || undefined} onValueChange={(v) => handleSelectChange("template_id", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona una plantilla" />
                    </SelectTrigger>
                    <SelectContent>
                      {templatesOptions.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => navigate("/campanas")} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? "Enviando..." : "Enviar"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
